// src/services/interviewAssistantService.js
import { fetchFromBackend, fetchWithFormData } from "./apiClient";

const SERVICE_PATH = "api/interview";

/**
 * Helper function to handle common error logging and re-throwing.
 * Logs the full error in development/test environments and throws a user-friendly error.
 * @param {string} functionName - The name of the function where the error occurred.
 * @param {Error} error - The original error object.
 * @param {string} userFriendlyMessage - The message to show to the user.
 */
const handleError = (functionName, error, userFriendlyMessage) => {
  // Log the full error in development or test environments for debugging.
  // In production, we avoid logging sensitive or internal error details to the console.
  if (process.env.NODE_ENV !== "production") {
    console.error(`[${functionName}] Detailed error:`, error);
  }
  // Throw a new error with a user-friendly message, hiding internal details.
  throw new Error(userFriendlyMessage);
};

/**
 * Analyzes CV text by calling the backend service and generating interview questions.
 * @param {string} cvText - The text of the CV.
 * @returns {Promise<object>} - The analysis result from the backend.
 */
export const generateQuestions = async (file, jobDescription) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (jobDescription) {
      formData.append("jobDescription", jobDescription);
    }
    return await fetchWithFormData(`${SERVICE_PATH}/analyze-cv`, formData);
  } catch (error) {
    handleError(
      "analyzeCv",
      error,
      "Failed to analyze your CV. Please check the content and try again."
    );
  }
};

/**
 * Evaluates a user's answer to an interview question.
 * @param {string} question - The interview question that was asked.
 * @param {object} cvContext - The CV analysis profile of the candidate.
 * @param {Blob} audioBlob - The audio recording of the user's spoken response.
 * @returns {Promise<object>} - The answer evaluation object.
 */
export const evaluateAnswer = async (questionId, sessionId, audioBlob) => {
  try {
    const formData = new FormData();
    // Provide a default filename if blob has no name (e.g., from MediaRecorder)
    formData.append("audioFile", audioBlob, audioBlob.name || "audio.webm");
    formData.append(
      "answerEvaluationRequest",
      JSON.stringify({
        questionId,
        sessionId,
      })
    );
    return await fetchWithFormData(`${SERVICE_PATH}/evaluate-answer`, formData);
  } catch (error) {
    handleError(
      "evaluateAnswer",
      error,
      "Failed to evaluate your answer. Please ensure your microphone is working and try again."
    );
  }
};

/**
 * Gets overall interview feedback based on all user responses.
 * @param {Array<object>} userResponses - Array of objects, each containing the question and its feedback.
 *   Example item: { question: { type: "Technical", question: "..." }, feedback: { overallAssessment: "...", ... } }
 * @param {string} [jobDescription=""] - The job description.
 * @returns {Promise<string>} - A string containing the overall feedback, likely in Markdown format.
 */
export const getOverallInterviewFeedback = async (sessionId) => {
  try {
    // The backend returns text/markdown, fetchFromBackend should handle this
    return await fetchFromBackend(`${SERVICE_PATH}/overall-feedback`, {
      body: {
        sessionId: sessionId,
      },
    });
  } catch (error) {
    handleError(
      "getOverallInterviewFeedback",
      error,
      "Failed to get overall interview feedback. Please try again later."
    );
  }
};

export const getInterviewSessions = async (page = 1, pageSize = 10) => {
  try {
    return await fetchFromBackend(
      `${SERVICE_PATH}/interview-sessions?page=${page}&pageSize=${pageSize}`,
      {},
      "GET"
    );
  } catch (error) {
    handleError(
      "getInterviewSessions",
      error,
      "Failed to get interview sessions. Please try again later."
    );
  }
};
/**
 * Gets the detailed information for a specific interview session, including all responses.
 * @param {string} sessionId - The ID of the interview session.
 * @returns {Promise<object>} - The session details including responses.
 * Expected response structure:
 * {
 *   sessionId: string,
 *   cvName: string | null,
 *   jobDescription: string | null,
 *   createdAt: string, // ISO date string
 *   overallFeedbackText: string | null, // Markdown for overall feedback
 *   responses: UserInterviewResponseResponse[] // From your C# record
 * }
 */
export const getInterviewSessionDetails = async (sessionId) => {
  try {
    return await fetchFromBackend(
      `${SERVICE_PATH}/sessions/${sessionId}`,
      {},
      "GET"
    );
  } catch (error) {
    handleError(
      "getInterviewSessionDetails",
      error,
      `Failed to fetch details for session ${sessionId}. Please try again.`
    );
    // Rethrow or return a specific error structure if needed by the page
    throw error; // Or return null / specific error object
  }
};

/**
 * Retrieves a list of previously uploaded CVs for the current user.
 * Each CV object contains its ID and name.
 * @returns {Promise<Array<object>>} - A promise that resolves to an array of CV objects, e.g., [{ id: 1, cvName: "MyResume.pdf" }].
 */
export const getCVs = async () => {
  try {
    return await fetchFromBackend(`${SERVICE_PATH}/cvs`, {
      method: "GET",
    });
  } catch (error) {
    handleError(
      "getCVs",
      error,
      "Failed to get saved CVs. Please try again later."
    );
  }
};

/**
 * Regenerates interview questions based on an existing CV and an optional job description.
 * @param {number} cvId - The ID of the existing CV session to use.
 * @param {string} [jobDescription] - An optional job description to tailor the questions.
 * @returns {Promise<Array<object>>} - A promise that resolves to a list of new interview questions.
 */
export const regenerateQuestionsFromCv = async (cvId, jobDescription) => {
  try {
    return await fetchFromBackend(
      `${SERVICE_PATH}/regenerate-questions-from-cv`,
      {
        body: { cvId, jobDescription },
      }
    );
  } catch (error) {
    handleError(
      "regenerateQuestionsFromCv",
      error,
      "Failed to regenerate questions from the selected CV. Please try again."
    );
  }
};
